import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { citiesApi } from '../../api/cities';

export default function CityList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('Europe/Berlin');

  const { data, isLoading, error } = useQuery({
    queryKey: ['cities'],
    queryFn: citiesApi.list,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      citiesApi.create({
        name: name.trim(),
        timezone: timezone.trim() || 'Europe/Berlin',
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      navigate(`/admin/cities/${created.id}`);
    },
  });

  if (isLoading) return <div className="p-6">Lädt…</div>;
  if (error) return <div className="p-6 text-red-600">Fehler: {(error as Error).message}</div>;

  const cities = data?.items ?? [];
  const canCreateCity = cities.length === 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Stadt</h1>
      </div>

      {canCreateCity && (
        <div className="mb-6 bg-white rounded shadow border border-gray-200 p-4 space-y-3">
          <h2 className="text-lg font-semibold">Erste Stadt anlegen</h2>
          <p className="text-sm text-gray-600">
            Pro Deployment ist genau eine Stadt vorgesehen. Sobald eine Stadt angelegt wurde,
            kann erst nach deren Löschung wieder eine neue erstellt werden.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Berlin"
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">Zeitzone</span>
              <input
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Europe/Berlin"
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </label>
          </div>
          {createMutation.error && (
            <div className="text-sm text-red-600">{(createMutation.error as Error).message}</div>
          )}
          <div className="flex justify-end">
            <button
              onClick={() => createMutation.mutate()}
              disabled={!name.trim() || createMutation.isPending}
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Anlegen…' : 'Stadt anlegen'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left w-12"></th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Zeitzone</th>
              <th className="px-4 py-2 text-right">Nutzer</th>
              <th className="px-4 py-2 text-right">Inserate</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {cities.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  Keine Stadt konfiguriert.
                </td>
              </tr>
            )}
            {cities.map((c) => (
              <tr
                key={c.id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/cities/${c.id}`)}
              >
                <td className="px-4 py-2">
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <div className="h-8 w-8" />
                  )}
                </td>
                <td className="px-4 py-2 font-medium">{c.name}</td>
                <td className="px-4 py-2 text-gray-600">{c.timezone}</td>
                <td className="px-4 py-2 text-right">{c.user_count}</td>
                <td className="px-4 py-2 text-right">{c.listing_count}</td>
                <td className="px-4 py-2">
                  {c.is_active ? (
                    <span className="text-green-700">aktiv</span>
                  ) : (
                    <span className="text-gray-400">inaktiv</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
